import { t } from "../../i18n";
import { useMemo, useState } from "react";
import type { WallInput } from "@tasduvar/engine";
import type { SoilExtractResponse, SoilExtraction } from "@tasduvar/shared";
import { api, ApiClientError } from "../../api/client";
import { useProjectStore } from "../../state/projectStore";
import { Button } from "../../ui/Button";
import { Select } from "../../ui/Select";
import { applyProposals, buildProposals, layerLabel, pickFoundationLayer, type Proposal } from "./applyExtraction";
import { useExtractCapabilities } from "./useExtractCapabilities";
import { toSoilExtraction } from "./localParser";
import { readLocalReport, REPORT_ACCEPT } from "./readLocalReport";

const GROUPS: { id: Proposal["group"]; label: string }[] = [
  { id: "foundation", label: t("Temel zemini") }, { id: "bearing", label: t("Taşıma gücü · taban sürtünmesi") }, { id: "backfill", label: t("Dolgu") },
  { id: "water", label: t("Yeraltı suyu") }, { id: "seismic", label: t("Deprem") }, { id: "site", label: t("Proje künyesi") },
];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(new Error("Dosya okunamadı"));
    r.readAsDataURL(file);
  });
}

/**
 * Zemin etüt raporundan (PDF) parametre çıkarma. Varsayılan: yerel kural tabanlı okuyucu (tarayıcıda pdf.js; yapay zekâ yok, dosya
 * gönderilmez). Sunucuda Gemini anahtarı varsa "Yapay zekâ ile oku" seçeneği çıkar. Akış: dosya → öneri tablosu (mevcut ↔ önerilen,
 * sayfa) → seçilenler duvara / künyeye yazılır. Otomatik yazma yok; kullanıcı her satırı onaylar.
 */
export function SoilReportDialog({ open, onClose, wall }: { open: boolean; onClose: () => void; wall: WallInput }): JSX.Element | null {
  const caps = useExtractCapabilities((s) => s.caps);
  const { project, updateWall, updateInfo } = useProjectStore();
  const [file, setFile] = useState<File | null>(null);
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [res, setRes] = useState<SoilExtractResponse | null>(null);
  const [layerIdx, setLayerIdx] = useState<number | null>(null);
  const [waterRef, setWaterRef] = useState<"backfillTop" | "frontGround">("backfillTop");
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [applyAll, setApplyAll] = useState(false);
  // Yapay zekâ yalnız sunucu açıksa seçilebilir; varsayılan yapay zekâ (varsa), yoksa yerel okuyucu
  const [aiPref, setAiPref] = useState<boolean | null>(null);
  const useAi = !!caps?.enabled && (aiPref ?? true);
  const x: SoilExtraction | null = res?.extraction ?? null;
  const li = x ? (layerIdx ?? pickFoundationLayer(x)) : -1;
  const proposals = useMemo(() => (x && project ? buildProposals(x, wall, project.info, { foundationLayer: li, waterReference: waterRef }) : []), [x, wall, project, li, waterRef]);
  const sel = selected ?? new Set(proposals.filter((p) => p.checked).map((p) => p.id));
  if (!open || !project) return null;

  const run = async (): Promise<void> => {
    if (!file) return;
    setBusy(true); setError(null); setRes(null); setSelected(null); setLayerIdx(null);
    try {
      if (!useAi) {
        const t0 = Date.now();
        const r = await readLocalReport(file);
        if (r.scanned) throw new Error(t("Taranmış PDF — metin okunamadı, değerleri elle girin."));
        setRes({ extraction: toSoilExtraction(r), model: "yerel", usage: { promptTokens: 0, outputTokens: 0 }, elapsedMs: Date.now() - t0 });
        return;
      }
      if (caps && file.size > caps.maxBytes) throw new Error(t("Dosya 20 MB sınırını aşıyor."));
      const mime = file.type === "application/pdf" || file.type.startsWith("image/") ? (file.type as "application/pdf") : "application/pdf";
      const dataBase64 = await fileToBase64(file);
      setRes(await api<SoilExtractResponse>("POST", "/extract/soil-report", { filename: file.name, mime, dataBase64, hint: hint.trim() || undefined }));
    } catch (e) {
      setError(e instanceof ApiClientError ? (e.code === "SUBSCRIPTION_REQUIRED" ? t("Bu özellik için aktif abonelik gerekir.") : e.code === "RATE_LIMITED" ? t("Saatlik çıkarma sınırına ulaştınız; biraz sonra deneyin.") : e.message) : (e as Error).message);
    } finally { setBusy(false); }
  };
  const toggle = (id: string): void => { const s = new Set(sel); if (s.has(id)) s.delete(id); else s.add(id); setSelected(s); };
  const apply = (): void => {
    const { wallRecipe, infoPatch, count } = applyProposals(proposals, sel);
    if (!count) return;
    const targets = applyAll ? project.walls.map((w) => w.id) : [wall.id];
    for (const id of targets) updateWall(id, wallRecipe);
    const info = structuredClone(project.info); infoPatch(info); updateInfo(info);
    onClose();
  };
  const reset = (): void => { setRes(null); setSelected(null); setError(null); };

  return (
    <div className="fixed inset-0 z-40 bg-black/30 grid place-items-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={t("Zemin etüt raporundan al")} className="card p-5 w-full max-w-3xl max-h-[90vh] overflow-auto grid gap-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold">{t("Zemin etüt raporundan al")}</h2>
          <span className="text-xs text-stone">{useAi ? <>{t("Yapay zekâ (Google Gemini) raporu okur; değerler onayınızla yazılır.")}{caps?.model ? ` · ${caps.model}` : ""}</> : t("Rapor bu bilgisayarda okunur (yapay zekâ yok, dosya gönderilmez); değerler onayınızla yazılır.")}</span>
        </div>
        {!x ? (
          <>
            <label className="grid gap-1 text-sm">
              <span>{useAi ? t("Rapor dosyası (PDF; taranmış PDF de olur)") : t("Rapor dosyası (metin katmanlı PDF)")}</span>
              <input type="file" accept={useAi ? "application/pdf,image/png,image/jpeg,image/webp" : REPORT_ACCEPT} aria-label={t("Rapor dosyası")} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            {caps?.enabled ? <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={useAi} onChange={(e) => setAiPref(e.target.checked)} />{t("Yapay zekâ ile oku (taranmış PDF ve karmaşık tablolar için)")}</label> : null}
            {useAi ? <>
              <label className="grid gap-1 text-sm">
                <span>{t("Not (isteğe bağlı) — ör. hangi sondaj / hangi tabaka")}</span>
                <input className="input font-sans" value={hint} onChange={(e) => setHint(e.target.value)} placeholder={t("SK-2 sondajı, temel 2,0 m'de")} aria-label={t("Not")} />
              </label>
              <p className="text-xs text-stone">{t("Dosya yalnız bu çıkarma için sunucuya ve Google'a gönderilir, saklanmaz. Kişisel veri içeren sayfaları göndermeden çıkarabilirsiniz.")}</p>
            </> : <p className="text-xs text-stone">{t("Yerel okuyucu Türkçe rapor terimlerini (γ, φ, c, taşıma gücü, zemin sınıfı, SS/S1, yeraltı suyu, tabaka tabloları) tanır. Belirsiz değerler önerilmez; uyarılarda gösterilir.")}</p>}
            {error ? <p role="alert" className="text-sm text-bad">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>{t("Vazgeç")}</Button>
              <Button onClick={() => void run()} disabled={!file || busy}>{busy ? (useAi ? t("Okunuyor… (30–90 sn)") : t("Okunuyor…")) : t("Raporu oku")}</Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-xs text-stone flex flex-wrap gap-x-3 gap-y-1">
              <span>{x.document.title ?? t("Başlık yok")}{x.document.firm ? ` — ${x.document.firm}` : ""}{x.document.date ? ` (${x.document.date})` : ""}</span>
              {x.document.pageCount ? <span>{x.document.pageCount} {t("sayfa")}</span> : null}
              <span>{res?.model === "yerel" ? t("yerel okuyucu (yapay zekâ yok)") : `${t("süre")} ${Math.round((res?.elapsedMs ?? 0) / 1000)} s`}</span>
            </div>
            {x.layers.length ? (
              <Select id="ex-layer" label={t("Temel zemini olarak alınacak tabaka")} value={String(li)} options={x.layers.map((l, i) => ({ value: String(i), label: layerLabel(l, i) }))} onChange={(v) => { setLayerIdx(Number(v)); setSelected(null); }} />
            ) : <p className="text-sm text-stone">{t("Raporda tabaka tablosu bulunamadı.")}</p>}
            {x.groundwater.depth !== null ? (
              <Select id="ex-water" label={t("Yeraltı suyu derinliği referansı")} value={waterRef} options={[{ value: "backfillTop", label: t("Dolgu üst yüzeyi (arka zemin)") }, { value: "frontGround", label: t("Ön zemin") }]} onChange={(v) => { setWaterRef(v); setSelected(null); }} hint={t("Sondaj ağzı hangi kotta ise onu seçin")} />
            ) : null}
            <table className="w-full text-sm" aria-label={t("Çıkarılan değerler")}>
              <thead><tr className="text-left text-xs text-stone border-b border-rule"><th className="py-1 w-8"></th><th>{t("Alan")}</th><th className="num">{t("Mevcut")}</th><th className="num">{t("Önerilen")}</th><th className="num">{t("Sayfa")}</th></tr></thead>
              <tbody>
                {GROUPS.map((g) => {
                  const rows = proposals.filter((p) => p.group === g.id);
                  if (!rows.length) return null;
                  return [
                    <tr key={`${g.id}-h`} className="bg-ink-light/50"><td colSpan={5} className="py-1 px-1 text-xs font-medium text-ink">{g.label}</td></tr>,
                    ...rows.map((p) => (
                      <tr key={p.id} className="border-b border-rule/60">
                        <td className="py-1 px-1"><input type="checkbox" checked={sel.has(p.id)} disabled={!p.apply && !p.applyInfo} onChange={() => toggle(p.id)} aria-label={`${p.label} ${t("uygula")}`} /></td>
                        <td className="py-1">{p.label}</td>
                        <td className="num text-stone">{p.current}</td>
                        <td className={`num ${p.current === p.proposed ? "text-stone" : "text-ink font-medium"}`}>{p.proposed}</td>
                        <td className="num text-stone">{p.page ?? ""}</td>
                      </tr>
                    )),
                  ];
                })}
              </tbody>
            </table>
            {x.warnings.length ? <ul className="text-xs text-warn list-disc pl-4" aria-label={t("Uyarılar")}>{x.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul> : null}
            {x.foundation.note || x.backfill.note || x.groundwater.note ? <p className="text-xs text-stone">{[x.foundation.note, x.backfill.note, x.groundwater.note].filter(Boolean).join(" · ")}</p> : null}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={applyAll} onChange={(e) => setApplyAll(e.target.checked)} />{t("Projedeki tüm duvarlara uygula")} ({project.walls.length})</label>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={reset}>{t("Başka dosya")}</Button>
                <Button variant="ghost" onClick={onClose}>{t("Vazgeç")}</Button>
                <Button onClick={apply} disabled={!sel.size}>{t("Seçilenleri uygula")} ({sel.size})</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
